/**
 * What every on-screen print preview needs (template thumbnails, the
 * Template Editor canvas, the sheet editor): the print engine and the font
 * registry, loaded lazily once — the engine pulls in pdf-lib + fontkit, which
 * the rest of the app never needs.
 */

import { useEffect, useState } from 'react';
import type { FontRegistry, SvgFontStyle } from '@pointorama/print-engine';
import type { OrgPrintFont } from '../fonts/printFonts';

export type PrintEngine = typeof import('../engine');

export interface PreviewEnv {
  engine: PrintEngine;
  fonts: FontRegistry;
  svgFont: (key: string) => SvgFontStyle;
}

const envCache = new Map<string, Promise<PreviewEnv>>();

/** One environment per set of organisation fonts (bundled fonts are shared). */
export function loadPreviewEnv(orgFonts: readonly OrgPrintFont[]): Promise<PreviewEnv> {
  const key = orgFonts.map((f) => `${f.id}@${f.url.split('?')[0]}`).join('|');
  let pending = envCache.get(key);
  if (!pending) {
    pending = (async () => {
      const [engine, { createPrintFontRegistry, previewFontStyles }] = await Promise.all([import('../engine'), import('../fonts/printFonts')]);
      const fonts = await createPrintFontRegistry(orgFonts);
      const svgFont = await previewFontStyles(fonts);
      return { engine, fonts, svgFont };
    })().catch((error: unknown) => {
      envCache.delete(key);
      throw error;
    });
    envCache.set(key, pending);
  }
  return pending;
}

export function usePreviewEnv(orgFonts: readonly OrgPrintFont[]): PreviewEnv | null {
  const [env, setEnv] = useState<PreviewEnv | null>(null);
  const key = orgFonts.map((f) => f.id).join('|');
  useEffect(() => {
    let cancelled = false;
    loadPreviewEnv(orgFonts)
      .then((loaded) => !cancelled && setEnv(loaded))
      .catch((error: unknown) => console.error('Print preview could not load its fonts', error));
    return () => {
      cancelled = true;
    };
    // The font list identity changes per render; its ids decide.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return env;
}
